import { userInfoAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { SuperBadgeSimple } from 'app/design/icons/badge-super-simple';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { Separator } from 'app/design/primitives/text-divider';
import { useNavigationOptions } from 'app/hooks/navigation';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { useStatistics } from 'app/network/statistics';
import type { NavigationScreen } from 'app/types/navigation';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { useUISize, usePageTitle } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai/index';
import { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';

const SuperPartageurDetailsScreenComponent: NavigationScreen<'super-partageur-details'> = ({ navigation }) => {
  const { t } = useTranslation('superPartageurDetails');

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );
  const setPageTitle = usePageTitle('superPartageurDetails');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  return (
    <Page>
      <SuperPartagerDetails />
    </Page>
  );
};

const SuperPartageurDetailsScreenWithErrorBoundary = withErrorBoundary(
  withDashboardLayout(SuperPartageurDetailsScreenComponent),
);

export const SuperPartageurDetailsScreen: NavigationScreen<'super-partageur-details'> = (props) => (
  <SuperPartageurDetailsScreenWithErrorBoundary {...props} />
);

export const SuperPartagerDetails = () => {
  const { t } = useTranslation('superPartageurDetails');
  const userInfo = useAtomValue(userInfoAtom);
  const statisticsQuery = useStatistics(userInfo?.id ?? '', { enabled: !!userInfo?.id });
  const { isLargeUI } = useUISize();

  const requirements = statisticsQuery.data?.isSuperRequirements ?? [
    { name: 'trustPoints', requiredValue: 5 },
    { name: 'responseRate', requiredValue: 90 },
    { name: 'lastActiveDays', requiredValue: 30 },
  ];
  const filteredRequirements = isWhiteLabel(WhiteLabel.Mutuo)
    ? requirements.filter((req) => req.name !== 'trustPoints')
    : requirements;

  return (
    <Column alignItems={isLargeUI ? 'flex-start' : 'center'} gap="spacing.general.x5">
      <Row alignItems="center" justifyContent={isLargeUI ? 'flex-start' : 'center'} width="100%">
        <SuperBadgeSimple />
        <Text fontWeight="700" variant="headerXSmall">
          {t('title2')}
        </Text>
      </Row>

      <Separator onlyWidth={isLargeUI} />

      <Text variant="bodyBase">{t('description')}</Text>

      {filteredRequirements.map((req) => (
        <Column alignItems={isLargeUI ? 'flex-start' : 'center'} gap="spacing.general.x5" key={req.name} width="100%">
          <Separator onlyWidth={isLargeUI} />

          <Column gap="spacing.general.x2" width="100%">
            <Text fontWeight="700" variant="bodyLarge">
              {t(`requirements.${req.name}.title`)}
            </Text>
            <Text variant="bodyBase">
              <Trans
                components={{ strong: <Text fontWeight="700" /> }}
                i18nKey={`requirements.${req.name}.description`}
                ns="superPartageurDetails"
                values={{ requiredValue: req.requiredValue }}
              />
            </Text>
          </Column>
        </Column>
      ))}
    </Column>
  );
};
